/**
 * Sync engine — drives push (outbox → server) and pull (server → cache) over a
 * {@link Repo}. Platform-agnostic: the network is behind a small SyncClient so
 * it unit-tests with a fake and runs identically on web and native.
 *
 * Cycle (runSync): push queued mutations → replay attachment blobs → pull delta
 * → reconcile. The server returns a per-mutation verdict; applied/superseded/
 * unauthorized all clear the outbox row (the follow-up pull brings canonical
 * truth; supersede also produced a server notification for the rejected actor).
 */
import { api, ApiError } from "@/lib/api-client";
import type { ApiOk, NegativeObservation, Inspection } from "@/types/api";
import type { Repo } from "./repo";
import { coalesceKey } from "./repo";
import type { OutboxMutation, OutboxEntity, PullDelta, PushResult } from "./types";

/** Build a {@link PullDelta} with only the rows we want to touch — applyDelta
 *  reads every array via optional chaining, so the empty ones are no-ops. */
function partialDelta(over: Partial<PullDelta>): PullDelta {
  return {
    serverTime: "", inspections: [], answers: [], aspects: [], humanRankResponses: [],
    photoComparisons: [], negativeObservations: [], rectifications: [], ranks: [], ...over,
  };
}

export interface SyncClient {
  push(body: { mutations: unknown[] }): Promise<{ serverTime: string; results: PushResult[] }>;
  pull(since: string | null): Promise<PullDelta>;
  uploadAttachment(path: string, formData: FormData, query: Record<string, string | number | undefined>): Promise<void>;
  /** Replay a queued offline submit against the real lifecycle endpoint.
   *  Optional so existing test fakes (which never queue a submit) compile. */
  submitInspection?(inspectionId: number): Promise<void>;
  /** Create an inspection via the normal REST endpoint (offline-create replay).
   *  Returns the server row; the endpoint is idempotent on payload.clientId. */
  createInspection(payload: Record<string, unknown>): Promise<Inspection>;
  /** Deficiency-finding REST replay (optional → test fakes compile). */
  createFinding?(inspectionId: number, payload: Record<string, unknown>): Promise<{ id: number }>;
  updateFinding?(inspectionId: number, findingId: number, payload: Record<string, unknown>): Promise<void>;
  deleteFinding?(inspectionId: number, findingId: number): Promise<void>;
  /** Delete a server attachment captured offline (optional → test fakes compile). */
  deleteAttachment?(attachmentId: number): Promise<void>;
  /** Custom-checklist per-question finding REST replay (optional → test fakes compile). */
  createQuestionFinding?(payload: Record<string, unknown>): Promise<{ id: number }>;
  deleteQuestionFinding?(id: number): Promise<void>;
}

/** Inspections per page for GET /api/sync/pull. Keeps each response ≈2 MB so a
 *  burst of concurrent pulls can't OOM a small (t3.nano-class) server. The
 *  client reassembles pages into one PullDelta below, so the rest of the sync
 *  engine (and its tests) never see the paging. */
const PAGE_LIMIT = 25;

/** Server /pull response: a PullDelta page plus the keyset cursor. */
type PullPage = ApiOk<PullDelta> & { hasMore?: boolean; nextAfter?: number };

/** Default SyncClient backed by the real API client. */
export const apiSyncClient: SyncClient = {
  async push(body) {
    const json = await api.post<ApiOk<{ serverTime: string; results: PushResult[] }>>("/api/sync/push", body);
    return json.data;
  },
  async pull(since) {
    // Page the delta by inspection id (keyset on the server) so no single
    // response is huge. Each page is a PARTIAL PullDelta; applyDelta is an
    // idempotent per-entity upsert, so concatenating pages yields the same
    // mirror state as one big delta. serverTime rides the first page (it's the
    // start-of-sequence cursor); tombstones ride the last. We assemble one
    // PullDelta here and the orchestrator stays oblivious to pagination.
    const merged = partialDelta({});
    let serverTime = "";
    let pageAfter = 0;
    for (;;) {
      const query: Record<string, string | number> = { pageLimit: PAGE_LIMIT, pageAfter };
      if (since) query.since = since;
      const json = await api.get<PullPage>("/api/sync/pull", { query });
      const page = json.data;
      if (page.serverTime && !serverTime) serverTime = page.serverTime;
      merged.inspections.push(...page.inspections);
      merged.answers.push(...page.answers);
      merged.aspects.push(...page.aspects);
      merged.humanRankResponses.push(...page.humanRankResponses);
      merged.photoComparisons.push(...page.photoComparisons);
      merged.negativeObservations.push(...page.negativeObservations);
      merged.rectifications.push(...page.rectifications);
      merged.ranks.push(...page.ranks);
      if (page.deletions?.length) (merged.deletions ??= []).push(...page.deletions);
      const next = json.nextAfter ?? pageAfter;
      // Stop when the server says there's no more, or the cursor fails to
      // advance (defensive against a loop — ids are strictly increasing).
      if (!json.hasMore || next <= pageAfter) break;
      pageAfter = next;
    }
    merged.serverTime = serverTime;
    return merged;
  },
  async uploadAttachment(path, formData, query) {
    await api.post<ApiOk<unknown>>(path, undefined, { formData, query });
  },
  async submitInspection(inspectionId) {
    await api.post<ApiOk<unknown>>(`/api/inspections/${inspectionId}/submit`);
  },
  async createInspection(payload) {
    const json = await api.post<ApiOk<Inspection>>("/api/inspections", payload);
    return json.data;
  },
  async createFinding(inspectionId, payload) {
    const json = await api.post<ApiOk<{ id: number }>>(`/api/inspections/${inspectionId}/findings`, payload);
    return json.data;
  },
  async updateFinding(inspectionId, findingId, payload) {
    await api.patch<ApiOk<unknown>>(`/api/inspections/${inspectionId}/findings/${findingId}`, payload);
  },
  async deleteFinding(inspectionId, findingId) {
    await api.delete<ApiOk<unknown>>(`/api/inspections/${inspectionId}/findings/${findingId}`);
  },
  async deleteAttachment(attachmentId) {
    await api.delete<ApiOk<unknown>>(`/api/attachments/${attachmentId}`);
  },
  async createQuestionFinding(payload) {
    const json = await api.post<ApiOk<{ id: number }>>("/api/negative-observations/question-finding", payload);
    return { id: json.data.id };
  },
  async deleteQuestionFinding(id) {
    await api.delete<ApiOk<unknown>>(`/api/negative-observations/question-finding/${id}`);
  },
};

/** An empty pull delta — spread and override only the fields you write. */
const EMPTY_DELTA: PullDelta = {
  serverTime: "", inspections: [], answers: [], aspects: [], humanRankResponses: [],
  photoComparisons: [], negativeObservations: [], rectifications: [], ranks: [],
};

function uuid(): string {
  return typeof crypto !== "undefined" && "randomUUID" in crypto
    ? crypto.randomUUID()
    : `id_${Date.now()}_${Math.random().toString(36).slice(2)}`;
}

/* ── enqueue helpers (used by the screens) ─────────────────────────────── */

async function enqueue(
  repo: Repo,
  entity: OutboxEntity,
  key: string,
  overlay: Pick<OutboxMutation, "inspectionId" | "questionId" | "aspect" | "rankId" | "negativeObservationId" | "deficiencyFindingId">,
  payload: Record<string, unknown>,
  clientId = uuid(),
) {
  await repo.enqueue({ entity, coalesceKey: key, ...overlay, payload: { ...payload, clientId }, clientId, clientUpdatedAt: new Date().toISOString() });
}

export const queue = {
  // Offline-created inspection. Insert-only (unique per clientId, like NOMs) so
  // it never coalesces. `tempId` is the negative local id the optimistic mirror
  // row + child mutations reference until the create syncs (see
  // replayInspectionCreates). The full create payload rides in `fields` and is
  // replayed verbatim to POST /api/inspections.
  inspection: (repo: Repo, tempId: number, fields: Record<string, unknown>, clientId: string = uuid()) => {
    return enqueue(repo, "inspection", `inspection:${clientId}`, { inspectionId: tempId }, fields, clientId);
  },
  envelope: (repo: Repo, inspectionId: number, questionId: number, fields: { value?: unknown; comment?: string | null; primaryAnswer?: unknown; naReason?: unknown; naComment?: unknown }) =>
    enqueue(repo, "envelope", coalesceKey.envelope(inspectionId, questionId), { inspectionId, questionId }, { inspectionId, questionId, ...fields }),
  aspect: (repo: Repo, inspectionId: number, questionId: number, aspect: "hardware" | "human" | "process", fields: Record<string, unknown>) =>
    enqueue(repo, "aspect", coalesceKey.aspect(inspectionId, questionId, aspect), { inspectionId, questionId, aspect }, { inspectionId, questionId, aspect, ...fields }),
  humanRank: (repo: Repo, inspectionId: number, questionId: number, rankId: number, fields: Record<string, unknown>) =>
    enqueue(repo, "humanRank", coalesceKey.humanRank(inspectionId, questionId, rankId), { inspectionId, questionId, rankId }, { inspectionId, questionId, rankId, ...fields }),
  photoComparison: (repo: Repo, inspectionId: number, questionId: number, fields: Record<string, unknown>) =>
    enqueue(repo, "photoComparison", coalesceKey.photoComparison(inspectionId, questionId), { inspectionId, questionId }, { inspectionId, questionId, ...fields }),
  nom: async (repo: Repo, inspectionId: number, questionId: number | undefined, fields: Record<string, unknown>, clientId: string = uuid()) => {
    // A caller-supplied (deterministic) clientId lets a re-save coalesce onto the
    // same queued NOM (coalesceKey embeds it) and the server dedupe on it — used
    // by the human-aspect auto-NOM so repeated offline saves don't pile up.
    await enqueue(repo, "nom", coalesceKey.nom(clientId), { inspectionId, questionId }, { inspectionId, questionId, ...fields }, clientId);
    // Optimistically mirror the NOM so the Observations list + the in-inspection
    // NOM panel show it IMMEDIATELY offline. Without this the row only appears
    // after a sync pulls the server-created NOM back, so an observation raised
    // at sea is invisible until reconnect. A negative id marks it temporary; the
    // clientId carried in metadata lets the sync evict it once the real NOM is
    // created (see evictSyncedTempNoms). Best-effort — never block the enqueue.
    try {
      // The warmed mirror row is the full inspection-detail DTO (the server
      // response, cached verbatim), so it carries the joined vessel/checklist
      // fields at runtime even though the base Inspection type doesn't declare
      // them — read them through a loose cast.
      const insp = (await repo.getInspection(inspectionId)) as
        | (Partial<{ vesselId: number | null; vesselName: string | null; vesselImo: string | null; type: NegativeObservation["inspectionType"]; checklist: { name: string | null } | null }>)
        | undefined;
      const now = new Date().toISOString();
      const temp = {
        id: -Math.floor(Date.now() + Math.random() * 1000),
        inspectionId,
        answerId: null, aspectResponseId: null, photoComparisonId: null,
        aspect: fields.aspect ?? "hardware", severity: fields.severity ?? "minor",
        riskRating: null, findingTypeId: null, status: "open", socSource: null,
        socHardwareId: fields.socHardwareId ?? null, socProcessId: fields.socProcessId ?? null,
        rankId: fields.rankId ?? null, nocHardwareId: fields.nocHardwareId ?? null,
        nocProcessId: fields.nocProcessId ?? null, nocPhotoId: fields.nocPhotoId ?? null,
        nocOtherText: null, pifIds: fields.pifIds ?? [],
        title: fields.title ?? "", description: fields.description ?? "",
        inspectorComment: null, locationDetail: fields.locationDetail ?? null,
        operatorResponse: null, operatorResponseBy: null, operatorResponseAt: null,
        raisedBy: null, closedBy: null, closedAt: null,
        reviewStatus: "pending", reviewedBy: null, reviewedAt: null, reviewNote: null,
        createdAt: now, updatedAt: now,
        questionCode: null, questionText: null,
        vesselId: insp?.vesselId ?? null, vesselName: insp?.vesselName ?? null,
        vesselImo: insp?.vesselImo ?? null, inspectionType: insp?.type ?? null,
        checklistName: insp?.checklist?.name ?? null,
        rectificationStatus: null, rectificationRejected: false,
        metadata: { clientId },
      } as unknown as NegativeObservation;
      await repo.applyDelta(partialDelta({ negativeObservations: [temp] }));
    } catch { /* optimistic mirror is best-effort */ }
  },
  inspectionSubmit: (repo: Repo, inspectionId: number) =>
    enqueue(repo, "inspectionSubmit", coalesceKey.inspectionSubmit(inspectionId), { inspectionId }, { inspectionId }),
  // Delete of a SERVER (already-synced) attachment, captured offline. Replayed as
  // DELETE /api/attachments/:id by replayAttachmentDeletes; coalesces on the
  // attachment id (deleting the same attachment twice is one op).
  attachmentDelete: (repo: Repo, inspectionId: number, attachmentId: number) =>
    enqueue(repo, "attachmentDelete", `attachmentDelete:${attachmentId}`, { inspectionId }, { inspectionId, attachmentId }),
  // Deficiency-log findings (USCG/PSC/Flag). Replayed against the REST finding
  // endpoints by replayFindings(); a create's negative tempId is remapped to the
  // server id for any later update/delete. Create is insert-only (unique per
  // clientId); update/delete coalesce on the target finding.
  findingCreate: (repo: Repo, inspectionId: number, tempId: number, fields: Record<string, unknown>, clientId: string = uuid()) =>
    enqueue(repo, "finding", `finding:${clientId}`, { inspectionId, deficiencyFindingId: tempId }, { op: "create", inspectionId, findingId: tempId, fields }, clientId),
  findingUpdate: (repo: Repo, inspectionId: number, findingId: number, fields: Record<string, unknown>) =>
    enqueue(repo, "finding", `finding:update:${findingId}`, { inspectionId, deficiencyFindingId: findingId }, { op: "update", inspectionId, findingId, fields }),
  findingDelete: (repo: Repo, inspectionId: number, findingId: number) =>
    enqueue(repo, "finding", `finding:delete:${findingId}`, { inspectionId, deficiencyFindingId: findingId }, { op: "delete", inspectionId, findingId }),

  questionFindingCreate: (repo: Repo, inspectionId: number, questionId: number, tempId: number, fields: Record<string, unknown>, clientId: string = uuid()) =>
    enqueue(repo, "questionFinding", `questionFinding:${clientId}`, { inspectionId, questionId, negativeObservationId: tempId }, { op: "create", inspectionId, questionId, findingId: tempId, fields }, clientId),
  questionFindingDelete: (repo: Repo, inspectionId: number, questionId: number, id: number) =>
    enqueue(repo, "questionFinding", `questionFinding:delete:${id}`, { inspectionId, questionId, negativeObservationId: id }, { op: "delete", inspectionId, questionId, findingId: id }),
  
  cancelQuestionFindingCreate: async (repo: Repo, tempId: number) => {
    const rows = await repo.listOutbox(["pending", "failed"]);
    const ids = rows
      .filter((r) =>
        r.entity === "questionFinding"
        && (r.payload as { op?: string }).op === "create"
        && (r.payload as { findingId?: number }).findingId === tempId
        && r.id != null)
      .map((r) => r.id!);
    if (ids.length) await repo.deleteOutbox(ids);
  },
  // Rectifications & root causes own either a NOM or a deficiency finding.
  rectification: (repo: Repo, ownerKind: "negative_observation" | "deficiency_finding", ownerId: number, op: "create" | "update", fields: Record<string, unknown>) => {
    const owner = ownerKind === "deficiency_finding" ? { deficiencyFindingId: ownerId } : { negativeObservationId: ownerId };
    return enqueue(repo, "rectification", coalesceKey.rectification(ownerKind, ownerId), owner, { op, ...owner, ...fields });
  },
  rootCause: (repo: Repo, ownerKind: "negative_observation" | "deficiency_finding", ownerId: number, fields: Record<string, unknown>, clientId: string = uuid()) => {
    const owner = ownerKind === "deficiency_finding" ? { deficiencyFindingId: ownerId } : { negativeObservationId: ownerId };
    return enqueue(repo, "rootCause", coalesceKey.rootCause(clientId), owner, { op: "create", ...owner, ...fields }, clientId);
  },
  deleteRootCause: (repo: Repo, ownerKind: "negative_observation" | "deficiency_finding", ownerId: number, rootCauseId: number) => {
    const owner = ownerKind === "deficiency_finding" ? { deficiencyFindingId: ownerId } : { negativeObservationId: ownerId };
    return enqueue(repo, "rootCause", coalesceKey.rootCauseDelete(rootCauseId), owner, { op: "delete", rootCauseId, ...owner });
  },
  updateRootCause: (repo: Repo, ownerKind: "negative_observation" | "deficiency_finding", ownerId: number, rootCauseId: number, fields: Record<string, unknown>) => {
    const owner = ownerKind === "deficiency_finding" ? { deficiencyFindingId: ownerId } : { negativeObservationId: ownerId };
    return enqueue(repo, "rootCause", coalesceKey.rootCauseUpdate(rootCauseId), owner, { op: "update", rootCauseId, ...owner, ...fields });
  },
};

/* ── push / pull / runSync ─────────────────────────────────────────────── */

let inflight = false;

export interface SyncSummary {
  pushed: number;
  superseded: number;
  unauthorized: number;
  pulled: boolean;
  /** Offline-created inspections that synced this cycle. */
  createdInspections?: number;
  /** Temp local inspection id → real server id, for the cycle's synced creates.
   *  Drives the "redirect a user sitting on a temp inspection" UX. */
  idmap?: Record<number, number>;
  /** Inspection ids whose offline attachments uploaded this cycle — the caller
   *  refreshes their aggregated attachment caches so the offline Attachments
   *  page reflects them without an explicit online visit. */
  attachmentInspectionIds?: number[];
  error?: string;
}

/**
 * Replay offline-created inspections as normal REST creates BEFORE pushing the
 * answer/NOM outbox, so every child mutation references a real inspection id by
 * push time. For each queued `inspection` create:
 *   • POST /api/inspections (idempotent on payload.clientId);
 *   • mirror the returned row, remap the temp id on every queued child mutation
 *     + attachment, evict the optimistic temp row, and drop the create from the
 *     outbox;
 *   • record temp→server in a kv id-map so a screen still on the temp id can
 *     redirect after sync.
 * A 4xx (e.g. PIQ already exists, access lost) marks the create failed so it
 * surfaces; a network/5xx error re-queues it and aborts (we're still offline).
 */
async function replayInspectionCreates(repo: Repo, client: SyncClient): Promise<{ created: number; idmap: Record<number, number> }> {
  const rows = (await repo.listOutbox(["pending", "failed"])).filter((m) => m.entity === "inspection");
  const idmap: Record<number, number> = {};
  let created = 0;
  for (const row of rows) {
    if (row.id == null) continue;
    const tempId = row.inspectionId;
    await repo.markOutbox([row.id], "syncing");
    try {
      const saved = await client.createInspection(row.payload);
      await repo.applyDelta({ ...EMPTY_DELTA, inspections: [saved] });
      if (tempId != null && tempId !== saved.id) {
        await repo.remapInspectionId(tempId, saved.id);
        await repo.evictInspection(tempId);
        await repo.kvPut(`offline:idmap:${tempId}`, saved.id);
        idmap[tempId] = saved.id;
      }
      await repo.deleteOutbox([row.id]);
      created++;
    } catch (err) {
      if (err instanceof ApiError && err.status >= 400 && err.status < 500) {
        await repo.markOutbox([row.id], "failed", err.message);
      } else {
        await repo.markOutbox([row.id], "pending", err instanceof Error ? err.message : "create failed");
        throw err; // network/5xx → still offline; abort the cycle
      }
    }
  }
  return { created, idmap };
}

// The ONLY entities /api/sync/push accepts (its body schema is a union of these).
// inspection / inspectionSubmit / finding are NOT row upserts — they replay
// against REST endpoints (replayInspectionCreates / replaySubmits / replayFindings).
// A whitelist (not a blacklist) means a non-push entity can NEVER leak into the
// batch: a single unknown entity makes the server reject the WHOLE body with a
// 400, which would strand every queued change. This bit us when a failed
// offline inspection-create (marked "failed" on a 4xx) sat in the outbox and
// poisoned every subsequent push.
const PUSH_ENTITIES = new Set<string>([
  "envelope", "aspect", "humanRank", "photoComparison", "nom", "rectification", "rootCause",
]);

async function pushOutbox(repo: Repo, client: SyncClient): Promise<{ results: PushResult[]; superseded: number; unauthorized: number }> {
  const rows = (await repo.listOutbox(["pending", "failed"])).filter(
    (r) =>
      PUSH_ENTITIES.has(r.entity) &&
      // Skip children of an unsynced offline-created inspection — their temp id
      // is still negative and the server rejects it (inspectionId must be > 0).
      // They sync once replayInspectionCreates remaps the parent to its real id.
      !(typeof r.inspectionId === "number" && r.inspectionId < 0) &&
      // Likewise skip rectification/root-cause rows still bound to an un-synced
      // offline finding (negative deficiencyFindingId in the payload) — they get
      // remapped to the real id once replayFindings creates it.
      !(typeof (r.payload as { deficiencyFindingId?: number }).deficiencyFindingId === "number"
        && (r.payload as { deficiencyFindingId?: number }).deficiencyFindingId! < 0),
  );
  if (rows.length === 0) return { results: [], superseded: 0, unauthorized: 0 };
  const ids = rows.map((r) => r.id!).filter((x) => x != null);
  await repo.markOutbox(ids, "syncing");
  try {
    const body = { mutations: rows.map((r) => ({ entity: r.entity, ...r.payload, clientUpdatedAt: r.clientUpdatedAt, seq: r.seq })) };
    const resp = await client.push(body);
    const byClient = new Map(resp.results.map((x) => [x.clientId, x]));
    const toDelete: number[] = [];
    const toFail: number[] = [];
    const discarded: string[] = [];
    let superseded = 0, unauthorized = 0;
    for (const r of rows) {
      const v = byClient.get(r.clientId);
      if (!v) {
        // The push SUCCEEDED (we have resp) but the server returned NO verdict
        // for this mutation. Two cases:
        //  • It still references a TEMP (negative) id the server can't resolve
        //    yet — e.g. a rectification/root-cause on an offline-created NOM.
        //    Its parent syncs first, then evictSyncedTempNoms / remap* rewrites
        //    the id and the next push acks it → RETAIN (mark failed, retry).
        //  • Otherwise the server skipped it as permanently INVALID (bad schema).
        //    The payload can't change, so retrying loops forever — a stuck "N
        //    pending" badge that re-pushes every interval → DISCARD it.
        // (A real network failure throws and re-queues via the catch below; this
        // path only runs on an actual server response.)
        const p = r.payload as { negativeObservationId?: number; deficiencyFindingId?: number; findingId?: number };
        const hasTempRef =
          (typeof r.inspectionId === "number" && r.inspectionId < 0) ||
          (typeof r.negativeObservationId === "number" && r.negativeObservationId < 0) ||
          (typeof r.deficiencyFindingId === "number" && r.deficiencyFindingId < 0) ||
          (typeof p.negativeObservationId === "number" && p.negativeObservationId < 0) ||
          (typeof p.deficiencyFindingId === "number" && p.deficiencyFindingId < 0) ||
          (typeof p.findingId === "number" && p.findingId < 0);
        if (r.id != null) {
          if (hasTempRef) toFail.push(r.id);
          else { toDelete.push(r.id); discarded.push(`${r.entity}[${r.clientId}]`); }
        }
        continue;
      }
      if (v.verdict === "superseded") superseded++;
      if (v.verdict === "unauthorized") unauthorized++;
      if (r.id != null) toDelete.push(r.id);
    }
    if (discarded.length) console.warn(`[sync] discarding ${discarded.length} unacknowledged mutation(s) (server skipped as invalid — won't retry): ${discarded.join(", ")}`);
    if (toDelete.length) await repo.deleteOutbox(toDelete);
    if (toFail.length) await repo.markOutbox(toFail, "failed", "Awaiting a dependency (temp id) — will retry after it syncs");
    return { results: resp.results, superseded, unauthorized };
  } catch (err) {
    await repo.markOutbox(ids, "failed", err instanceof Error ? err.message : "push failed");
    throw err;
  }
}

async function replayAttachments(repo: Repo, client: SyncClient, applied: PushResult[]): Promise<Set<number>> {
  // Inspection ids whose attachments uploaded this cycle — the caller refreshes
  // their aggregated attachment caches (the offline Attachments page) so a file
  // added offline shows up without an explicit online visit to that page.
  const uploadedInspectionIds = new Set<number>();
  const pending = await repo.listAttachments(["pending", "failed"]);
  if (pending.length === 0) return uploadedInspectionIds;
  // Map an offline-created NOM's clientId → its new server id (for NOM-owned blobs).
  const nomServerId = new Map<string, number>();
  for (const r of applied) if (r.entity === "nom" && r.serverId != null) nomServerId.set(r.clientId, r.serverId);

  for (const att of pending) {
    if (att.id == null) continue;
    await repo.markAttachment(att.id, "syncing");
    try {
      const fd = new FormData();
      fd.append("files", att.blob, att.filename || "upload");
      let path: string;
      let query: Record<string, string | number | undefined>;
      if (att.ownerKind === "negative_observation") {
        // Resolve the parent NOM's server id: from THIS push's results, or — if
        // the NOM was created in a PRIOR sync — from the mirror by matching the
        // clientId the server stamps into the NOM's metadata. Without the mirror
        // fallback a NOM-owned attachment whose parent already synced would stay
        // stuck on "Parent observation not yet synced" forever.
        let ownerId = att.ownerClientId ? nomServerId.get(att.ownerClientId) : undefined;
        if (ownerId == null && att.ownerClientId) {
          const noms = await repo.listAllNegativeObservations();
          const match = noms.find(
            (n) => n.id > 0 && (n as { metadata?: { clientId?: string } }).metadata?.clientId === att.ownerClientId,
          );
          ownerId = match?.id;
        }
        if (ownerId == null) { await repo.markAttachment(att.id, "failed", "Parent observation not yet synced"); continue; }
        path = "/api/attachments";
        query = { ownerType: "negative_observation", ownerId, category: att.category ?? undefined };
      } else if (att.ownerKind === "generic") {
        // Directly-owned by a real row (finding / finding-evidence / observation)
        // via its numeric server id + explicit ownerType. An owner created offline
        // (temp/negative id) can't take an upload until it has synced.
        if (att.ownerId == null || att.ownerId < 0 || !att.attachmentOwnerType) { await repo.markAttachment(att.id, "failed", "Owner not yet synced"); continue; }
        path = "/api/attachments";
        query = { ownerType: att.attachmentOwnerType, ownerId: att.ownerId, category: att.category ?? undefined };
      } else {
        path = att.ownerKind === "aspect" ? "/api/attachments/aspect" : att.ownerKind === "photo_comparison" ? "/api/attachments/photo-comparison" : "/api/attachments/question";
        query = { inspectionId: att.inspectionId, questionId: att.questionId ?? undefined, ...(att.ownerKind === "aspect" && att.aspect ? { aspect: att.aspect } : {}), category: att.category ?? undefined, ...(att.transcript ? { transcript: att.transcript } : {}) };
      }
      await client.uploadAttachment(path, fd, query);
      await repo.deleteAttachment(att.id); // success → drop the blob
      if (typeof att.inspectionId === "number" && att.inspectionId > 0) uploadedInspectionIds.add(att.inspectionId);
    } catch (err) {
      // A permanent client rejection (unsupported type e.g. SVG, too large,
      // owner gone) will NEVER succeed on retry — drop it so it doesn't sit
      // "failed" forever and inflate the pending badge (mirrors the 4xx=drop
      // handling in replayFindings / replaySubmits). 408/429 are transient
      // (timeout / rate-limit) and >=500 is server-side → keep those for retry.
      const status = err instanceof ApiError ? err.status : (err as { status?: number }).status;
      if (typeof status === "number" && status >= 400 && status < 500 && status !== 408 && status !== 429) {
        console.warn(`[sync] discarding attachment (id=${att.id}, ${att.filename}): permanently rejected (${status}) — ${err instanceof Error ? err.message : "upload failed"}`);
        await repo.deleteAttachment(att.id);
      } else {
        await repo.markAttachment(att.id, "failed", err instanceof Error ? err.message : "upload failed");
      }
    }
  }
  return uploadedInspectionIds;
}

/** Drop the optimistic temp NOMs (negative ids) once their real server rows
 *  have been created, so the follow-up pull's canonical NOM doesn't duplicate
 *  the placeholder the user already saw offline. Matched by the clientId both
 *  rows carry in metadata. */
async function evictSyncedTempNoms(repo: Repo, applied: PushResult[]): Promise<void> {
  const byClient = new Map(
    applied
      .filter((r) => r.entity === "nom" && r.verdict !== "unauthorized" && r.serverId != null)
      .map((r) => [r.clientId, r.serverId!] as const),
  );
  if (byClient.size === 0) return;
  const all = await repo.listAllNegativeObservations();
  const stale = all.filter(
    (n) => n.id < 0 && byClient.has((n as { metadata?: { clientId?: string } }).metadata?.clientId ?? ""),
  );
  if (stale.length === 0) return;
  // Remap dependent outbox rows (rootCause / rectification) from the temp NOM id
  // to the real server id BEFORE evicting the temp row — otherwise they keep the
  // negative id forever and the server rejects them ("must be > 0").
  for (const n of stale) {
    const serverId = byClient.get((n as { metadata?: { clientId?: string } }).metadata?.clientId ?? "");
    if (serverId != null) await repo.remapNegativeObservationId(n.id, serverId);
  }
  await repo.applyDelta(partialDelta({ deletions: stale.map((n) => ({ entity: "negativeObservation" as const, entityId: n.id })) }));
}

/** Drop rootCause / rectification rows that point at a temp (negative) NOM id
 *  for which NO temp NOM remains in the mirror — i.e. the parent NOM already
 *  synced and its temp row was evicted, but this child predates the remap fix so
 *  it was never repointed. It can NEVER sync (the server rejects the negative
 *  id), so it would sit "unsynced" forever and block nothing but the count.
 *  Going forward evictSyncedTempNoms remaps these, so this only clears legacy
 *  orphans. */
async function dropOrphanedNomChildren(repo: Repo): Promise<number> {
  const rows = await repo.listOutbox(["pending", "failed"]);
  const candidates = rows.filter(
    (r) => (r.entity === "rootCause" || r.entity === "rectification")
      && typeof r.negativeObservationId === "number" && r.negativeObservationId < 0,
  );
  if (candidates.length === 0) return 0;
  const tempNomIds = new Set((await repo.listAllNegativeObservations()).filter((n) => n.id < 0).map((n) => n.id));
  const orphans = candidates.filter((r) => !tempNomIds.has(r.negativeObservationId!));
  for (const o of orphans) {
    if (o.id == null) continue;
    console.warn(`[sync] discarding orphaned ${o.entity} (clientId=${o.clientId}): parent NOM ${o.negativeObservationId} already synced but this child was never remapped`);
    await repo.deleteOutbox([o.id]);
  }
  return orphans.length;
}

/** Drop queued (generic) attachments whose owner is a temp (negative) id that
 *  no longer maps to anything that could ever remap it — i.e. the parent NOM /
 *  finding already synced and its temp row was evicted, but this upload's
 *  numeric ownerId was never repointed (legacy rows from before the remap fix in
 *  remapNegativeObservationId). Such an attachment can NEVER upload (the server
 *  rejects the negative ownerId), so it would sit "failed" forever and inflate
 *  the pending badge. An attachment is KEPT if some temp NOM row still exists or
 *  an outbox row still references the temp id (a future replay/evict will remap
 *  it). Going forward the remap functions repoint these, so this only clears
 *  stranded legacy rows. */
async function dropOrphanedGenericAttachments(repo: Repo): Promise<number> {
  const atts = (await repo.listAttachments(["pending", "failed"])).filter(
    (a) => a.ownerKind === "generic" && typeof a.ownerId === "number" && a.ownerId < 0,
  );
  if (atts.length === 0) return 0;
  const tempNomIds = new Set((await repo.listAllNegativeObservations()).filter((n) => n.id < 0).map((n) => n.id));
  const outbox = await repo.listOutbox(["pending", "failed"]);
  const stillReferenced = (id: number) =>
    tempNomIds.has(id) ||
    outbox.some((r) => {
      const p = r.payload as { findingId?: number; deficiencyFindingId?: number };
      return r.inspectionId === id || r.negativeObservationId === id || r.deficiencyFindingId === id
        || p.findingId === id || p.deficiencyFindingId === id;
    });
  let dropped = 0;
  for (const a of atts) {
    if (stillReferenced(a.ownerId!)) continue; // a future replay/evict will remap it
    if (a.id == null) continue;
    console.warn(`[sync] discarding orphaned ${a.attachmentOwnerType} attachment (id=${a.id}, ownerId=${a.ownerId}): owner already synced but the upload's ownerId was never remapped`);
    await repo.deleteAttachment(a.id);
    dropped++;
  }
  return dropped;
}

/** Replay queued offline submits against the real lifecycle endpoint, AFTER the
 *  data mutations have synced — so the server re-validates against fresh rows.
 *  A 4xx means the submit will never succeed on retry (e.g. the server's gate
 *  disagrees), so drop it; transient/network errors keep it queued. */
async function replaySubmits(repo: Repo, client: SyncClient): Promise<void> {
  if (!client.submitInspection) return;
  const rows = (await repo.listOutbox(["pending", "failed"])).filter((r) => r.entity === "inspectionSubmit");
  for (const r of rows) {
    if (r.id == null) continue;
    const inspectionId = Number((r.payload as { inspectionId?: number }).inspectionId);
    if (!inspectionId) { await repo.deleteOutbox([r.id]); continue; }
    try {
      await client.submitInspection(inspectionId);
      await repo.deleteOutbox([r.id]);
    } catch (err) {
      const status = (err as { status?: number }).status;
      if (typeof status === "number" && status >= 400 && status < 500) {
        await repo.deleteOutbox([r.id]); // permanent — won't succeed on retry
      } else {
        await repo.markOutbox([r.id], "failed", err instanceof Error ? err.message : "submit replay failed");
      }
    }
  }
}

/** Replay queued attachment DELETES against DELETE /api/attachments/:id. A 404
 *  means the row is already gone on the server → treat as done; other 4xx are
 *  permanent (drop); transient/network errors keep it queued for the next cycle.
 *  A negative id would be a queued-but-never-uploaded attachment (its upload was
 *  already dropped at delete time) — there's nothing on the server, so just clear
 *  the row. */
async function replayAttachmentDeletes(repo: Repo, client: SyncClient): Promise<void> {
  if (!client.deleteAttachment) return;
  const rows = (await repo.listOutbox(["pending", "failed"])).filter((r) => r.entity === "attachmentDelete");
  for (const r of rows) {
    if (r.id == null) continue;
    const attachmentId = Number((r.payload as { attachmentId?: number }).attachmentId);
    if (!attachmentId || attachmentId < 0) { await repo.deleteOutbox([r.id]); continue; }
    try {
      await client.deleteAttachment(attachmentId);
      await repo.deleteOutbox([r.id]);
    } catch (err) {
      const status = (err as { status?: number }).status;
      if (typeof status === "number" && status >= 400 && status < 500) {
        await repo.deleteOutbox([r.id]); // 404 already-gone / other permanent → done
      } else {
        await repo.markOutbox([r.id], "failed", err instanceof Error ? err.message : "attachment delete replay failed");
      }
    }
  }
}

/** Replay queued deficiency-finding ops against the REST finding endpoints, in
 *  seq order. An offline-created finding's negative temp id is remapped to the
 *  server id for any later update/delete in the same batch (create+delete
 *  offline collapses to a server delete of the just-created row). 4xx = drop
 *  (won't succeed on retry); transient = keep queued. */
async function replayFindings(repo: Repo, client: SyncClient): Promise<void> {
  if (!client.createFinding || !client.updateFinding || !client.deleteFinding) return;
  const rows = (await repo.listOutbox(["pending", "failed"]))
    .filter((r) => r.entity === "finding")
    .sort((a, b) => (a.seq ?? 0) - (b.seq ?? 0));
  const idmap = new Map<number, number>(); // temp (negative) findingId → server id
  for (const r of rows) {
    if (r.id == null) continue;
    const p = r.payload as { op: "create" | "update" | "delete"; inspectionId: number; findingId: number; fields?: Record<string, unknown> };
    const resolvedId = p.findingId < 0 ? idmap.get(p.findingId) : p.findingId;
    try {
      if (p.op === "create") {
        const created = await client.createFinding(p.inspectionId, p.fields ?? {});
        idmap.set(p.findingId, created.id);
        // Rewrite the temp finding id in dependent outbox rows (rectification /
        // rootCause) + queued attachments so they upload against the real finding
        // when pushOutbox / replayAttachments run (which is why findings replay
        // BEFORE the push — see runSync).
        if (p.findingId < 0) {
          await repo.remapFindingId(p.findingId, created.id);
          // Drop the optimistic temp row from the local findings caches so the
          // Observations list shows only the pulled real finding (with vessel
          // name) — not a duplicate temp row. Mirrors evictSyncedTempNoms.
          await repo.evictQueuedFinding(p.findingId);
        }
      } else if (p.op === "update") {
        if (resolvedId != null) await client.updateFinding(p.inspectionId, resolvedId, p.fields ?? {});
        // else: a temp create that never synced (shouldn't happen — create runs first) → drop
      } else if (p.op === "delete") {
        if (resolvedId != null) await client.deleteFinding(p.inspectionId, resolvedId);
        // else: created + deleted offline before any sync → nothing on the server
      }
      await repo.deleteOutbox([r.id]);
    } catch (err) {
      const status = (err as { status?: number }).status;
      if (typeof status === "number" && status >= 400 && status < 500) {
        await repo.deleteOutbox([r.id]); // permanent
      } else {
        await repo.markOutbox([r.id], "failed", err instanceof Error ? err.message : "finding replay failed");
      }
    }
  }
}

async function remapQuestionFindingKv(repo: Repo, inspectionId: number, questionId: number, tempId: number, realId: number): Promise<void> {
  try {
    const kvKey = `question-findings:${inspectionId}:${questionId}`;
    const arr = await repo.kvGet<Array<{ id: number }>>(kvKey);
    if (Array.isArray(arr)) {
      await repo.kvPut(kvKey, arr.map((f) => (f.id === tempId ? { ...f, id: realId } : f)));
    }
  } catch { /* optimistic reconciliation is best-effort */ }
}

async function replayQuestionFindings(repo: Repo, client: SyncClient): Promise<void> {
  if (!client.createQuestionFinding || !client.deleteQuestionFinding) return;
  const rows = (await repo.listOutbox(["pending", "failed"]))
    .filter((r) => r.entity === "questionFinding")
    .sort((a, b) => (a.seq ?? 0) - (b.seq ?? 0));
  for (const r of rows) {
    if (r.id == null) continue;
    const p = r.payload as { op: "create" | "delete"; inspectionId: number; questionId: number; findingId: number; fields?: Record<string, unknown> };
   
    if (typeof p.inspectionId === "number" && p.inspectionId < 0) continue;
    try {
      if (p.op === "create") {
        const created = await client.createQuestionFinding!({ ...(p.fields ?? {}), inspectionId: p.inspectionId, questionId: p.questionId });
        if (p.findingId < 0) await remapQuestionFindingKv(repo, p.inspectionId, p.questionId, p.findingId, created.id);
      } else if (p.findingId > 0) {
        await client.deleteQuestionFinding!(p.findingId);
      }
      await repo.deleteOutbox([r.id]);
    } catch (err) {
      const status = (err as { status?: number }).status;
      if (typeof status === "number" && status >= 400 && status < 500) {
        await repo.deleteOutbox([r.id]); // permanent (won't succeed on retry)
      } else {
        await repo.markOutbox([r.id], "failed", err instanceof Error ? err.message : "question-finding replay failed");
      }
    }
  }
}

async function pull(repo: Repo, client: SyncClient): Promise<void> {
  const since = await repo.getCursor();
  const delta = await client.pull(since);
  await repo.applyDelta(delta);
  // Keep the by-owner rectification kv snapshots (what readThrough serves
  // offline) coherent with the mirror. applyDelta updates the normalized rows,
  // but readThrough prefers the kv DTO and only falls back to the mirror when
  // the key is ABSENT — so without this, a pulled status change (e.g. an admin
  // sends a rectification back to the Master → "returned") stays invisible
  // offline behind the stale kv, and the Master's edit/root-cause controls
  // never unlock until a manual online refresh re-writes the key.
  if (delta.rectifications?.length) {
    const all = await repo.listRectifications();
    const nomIds = new Set<number>();
    const findingIds = new Set<number>();
    for (const r of delta.rectifications) {
      if (r.negativeObservationId != null) nomIds.add(r.negativeObservationId);
      if (r.deficiencyFindingId != null) findingIds.add(r.deficiencyFindingId);
    }
    for (const id of nomIds) await repo.kvPut(`rectifications:by-nom:${id}`, all.filter((r) => r.negativeObservationId === id));
    for (const id of findingIds) await repo.kvPut(`rectifications:by-finding:${id}`, all.filter((r) => r.deficiencyFindingId === id));
  }
  if (delta.serverTime) await repo.setCursor(delta.serverTime);
}

/** Full sync: push → replay attachments → pull. Re-entrant-safe (no-op if a
 *  sync is already running). Never throws — returns a summary with `error`. */
export async function runSync(repo: Repo, client: SyncClient = apiSyncClient): Promise<SyncSummary> {
  if (inflight) return { pushed: 0, superseded: 0, unauthorized: 0, pulled: false };
  inflight = true;
  try {
    // Replay offline-created inspections first so their queued children carry
    // real server ids before the push below.
    const { created, idmap } = await replayInspectionCreates(repo, client);
    // Create offline findings (REST) BEFORE the push so their temp ids are
    // remapped in dependent rectifications/root-causes/attachments — otherwise
    // those would push/upload against a dead negative finding id.
    await replayFindings(repo, client);
    // Custom-checklist per-question findings — self-contained REST replay (no
    // dependent outbox rows), so order vs the push doesn't matter; grouped with
    // the other finding replay for locality.
    await replayQuestionFindings(repo, client);
    const { results, superseded, unauthorized } = await pushOutbox(repo, client);
    const attachmentInspectionIds = await replayAttachments(repo, client, results);
    await evictSyncedTempNoms(repo, results);
    await dropOrphanedNomChildren(repo); // clear legacy orphans (parent NOM synced before the remap fix)
    await dropOrphanedGenericAttachments(repo); // clear stranded generic uploads whose temp owner is gone
    await replaySubmits(repo, client);
    await replayAttachmentDeletes(repo, client); // DELETE server attachments removed offline
    await pull(repo, client);
    return { pushed: results.length, superseded, unauthorized, pulled: true, createdInspections: created, idmap, attachmentInspectionIds: [...attachmentInspectionIds] };
  } catch (err) {
    // A push failure still leaves rows queued (marked failed) for next time.
    try { await pull(repo, client); } catch { /* offline */ }
    return { pushed: 0, superseded: 0, unauthorized: 0, pulled: false, error: err instanceof Error ? err.message : "sync failed" };
  } finally {
    inflight = false;
  }
}
